import { dinero, toDecimal } from 'dinero.js';
import { BRL } from 'dinero.js/currencies';

export type MoneyCents = number;

const brlFormatter = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const normalizeMoneyText = (value: unknown): string | null => {
  if (value === undefined || value === null || value === '') {
    return null;
  }

  let text = String(value)
    .trim()
    .replace(/\u00a0/g, ' ')
    .replace(/R\$/gi, '')
    .replace(/\s+/g, '');

  if (!text) {
    return null;
  }

  let sign = '';

  if (text.startsWith('-')) {
    sign = '-';
    text = text.slice(1);
  } else if (text.startsWith('+')) {
    text = text.slice(1);
  }

  if (!text) {
    return null;
  }

  if (text.includes(',')) {
    // Brazilian form: 8.500,25
    text = text.replace(/\./g, '').replace(',', '.');
  } else {
    const dotCount = (text.match(/\./g) ?? []).length;

    if (dotCount > 1) {
      // 8.500.000
      text = text.replace(/\./g, '');
    } else if (dotCount === 1) {
      const [, decimals = ''] = text.split('.');

      // A single dot followed by more than 2 digits is treated
      // as a thousands separator rather than a decimal separator.
      if (decimals.length > 2) {
        text = text.replace('.', '');
      }
    }
  }

  return `${sign}${text}`;
};

export const parseMoneyToCents = (value: unknown): MoneyCents | null => {
  const normalized = normalizeMoneyText(value);

  if (!normalized || !/^[+-]?\d+(?:\.\d{1,2})?$/.test(normalized)) {
    return null;
  }

  const negative = normalized.startsWith('-');
  const unsigned = normalized.replace(/^[+-]/, '');
  const [wholePart, decimalPart = ''] = unsigned.split('.');

  const whole = Number(wholePart);
  const fraction = Number(decimalPart.padEnd(2, '0'));

  if (!Number.isSafeInteger(whole) || !Number.isSafeInteger(fraction)) {
    return null;
  }

  const cents = whole * 100 + fraction;

  if (!Number.isSafeInteger(cents)) {
    return null;
  }

  return negative ? -cents : cents;
};

export const centsToDecimalString = (cents: MoneyCents): string => {
  if (!Number.isSafeInteger(cents)) {
    throw new TypeError('O valor monetário em centavos deve ser um inteiro seguro.');
  }

  const negative = cents < 0;
  const absolute = Math.abs(cents);
  const whole = Math.floor(absolute / 100);
  const fraction = String(absolute % 100).padStart(2, '0');

  return `${negative ? '-' : ''}${whole}.${fraction}`;
};

export const centsToMajorNumber = (cents: MoneyCents): number =>
  Number(centsToDecimalString(cents));

export const formatBRLCents = (cents: MoneyCents): string => {
  if (!Number.isSafeInteger(cents)) {
    throw new TypeError('O valor monetário em centavos deve ser um inteiro seguro.');
  }

  const value = dinero({
    amount: cents,
    currency: BRL,
  });

  return brlFormatter.format(Number(toDecimal(value)));
};

export const ensureMoneyCents = (
  value: unknown,
  fallback: MoneyCents = 0
): MoneyCents => {
  const cents = parseMoneyToCents(value);
  return cents ?? fallback;
};